import { ISuggestionEntry } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import {
  IFlattenedSearchProductsRequest,
  ISuggestionSearchDataType,
} from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { ISuggestionSections } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { shouldShowSearchRecommendations } from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/utils";
import {
  MAX_DESKTOP_SUGGESTION_QUERY_OVERFLOW,
  MAX_SUGGESTION_ITEMS_PER_SECTION,
  MAX_SEARCH_RECOMMENDATION_PER_SECTION,
  MAX_SECTION_RESULT_SIZE,
  MAX_SPLIT_SECTION_RESULT_SIZE,
  MAX_SUGGESTION_BRAND_SUGGESTIONS,
  MAX_SUGGESTION_TOP_RESULTS,
  MAX_DESKTOP_SUGGESTION_BRANDS_OVERFLOW,
  getBrandsHeader,
  getNaturalLanguageSearchEmptyStateHeader,
  getRecentSearchesHeader,
  getSuggestedTermsHeader,
  getTrendingSearchesHeader,
  getTopResultsHeader,
} from "@faire/deprecated-retailer-visitor-shared/components/TopSearch/ResultDropdown/consts";
import { BRAND_VALUES } from "@faire/deprecated-retailer-visitor-shared/consts/BRAND_VALUES";
import { decodeMultimodalQueryText } from "@faire/deprecated-retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { localizedUrlWithQueryParams } from "@faire/deprecated-retailer-visitor-shared/lib/pathUtils";
import { discoverSearch } from "@faire/deprecated-retailer-visitor-shared/lib/seoPaths";
import { brandPage } from "@faire/deprecated-retailer-visitor-shared/paths/index";
import { getSettingMaxNumBrandSuggestionsConfig } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingMaxNumBrandSuggestionsConfig";
import { getSettingMaxNumSearchQuerySuggestionsConfig } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingMaxNumSearchQuerySuggestionsConfig";
import { buildFilterKeys } from "@faire/retailer-shared/filters/filterKeys";
import { ISearchMakersWithFiltersRequest } from "@faire/web-api--source/indigofair/data/ISearchMakersWithFiltersRequest";
import { ISearchProductsRequest } from "@faire/web-api--source/indigofair/data/ISearchProductsRequest";
import { ISearchRequestReferrer } from "@faire/web-api--source/indigofair/data/ISearchRequestReferrer";
import { ISearchSuggestionResponse } from "@faire/web-api--source/indigofair/data/ISearchSuggestionResponse";
import { ISuggestedBrand } from "@faire/web-api--source/indigofair/data/ISuggestedBrand";
import { ISuggestedProduct } from "@faire/web-api--source/indigofair/data/ISuggestedProduct";
import { path as c1CategoryPath } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import productSearch from "@faire/web-api--source/routes/www/search";
import { StrictLocalizeFunction } from "@faire/web/common/localization/localize";
import { Viewport } from "@faire/web/ui/hooks/viewport-utils/viewport-utils";

/**
 * Build the query with appropriate filters for use in productSearch() later
 * @param {IFlattenedSearchProductsRequest} queryValue  The original SearchProductsRequest
 * @param {string}                          refReqId    The request ID for tracking purposes
 * @param {string|undefined}                filters     The filters to be applied in the query
 * @returns {string}                                    A string URL
 */
const buildLocalizedUrlWithQueryParams = (
  queryValue: IFlattenedSearchProductsRequest,
  refReqId: string | undefined,
  filters: string | undefined,
  strictLocalize: StrictLocalizeFunction
): string => {
  return localizedUrlWithQueryParams(
    productSearch({
      q: queryValue.query ?? "",
      // TODO(cedo): Clean this up. I think this is legacy code now.
      // We now use "filters" query param for filtering the search, but
      // we also don't preserve filters when searching with a new query.
      filter: Object.keys(BRAND_VALUES(strictLocalize)).reduce<
        string | undefined
      >(
        // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        (result, value) => (queryValue[value] ? value : result),
        undefined
      ),
      filters,
    }),
    {
      refType:
        ISearchRequestReferrer.SearchRequestReferrerType
          .SUGGESTIONS_SEARCH_QUERIES,
      refReqId,
    }
  );
};

const flattenQueryObjectValues = (
  queryObjectValues: ISearchProductsRequest[],
  queryObjectKeys: string[],
  maxNumberOfQuerySuggestions: number
) => {
  return queryObjectValues
    .flatMap(({ query, categories, autocomplete_categories }, index) => {
      if (autocomplete_categories?.length) {
        // Make an instance of IFlattenedSearchProductsRequest for every category found in autocomplete_categories
        return autocomplete_categories.map((autocomplete_category) => ({
          queryKey: queryObjectKeys[index],
          query,
          categories,
          ...autocomplete_category,
        }));
      }

      // If autocomplete_categories is empty, return instance of
      // IFlattenedSearchProductsRequest with undefined category_display_name and category_key
      return {
        queryKey: queryObjectKeys[index],
        query,
        categories,
        autocomplete_categories,
        category_display_name: undefined,
        category_key: undefined,
      };
    })
    .slice(0, maxNumberOfQuerySuggestions);
};

/**
 * Construct an ISuggestionEntry[] for suggested search queries
 * @param   {ISearchProductsRequest} queryObject Object with search suggestions
 * @param   {string}                 sectionName Name of the section
 * @returns {ISuggestionEntry}                   An array of processed search suggestions
 */
const buildQueriesSuggestionArray = ({
  queryObject,
  sectionName,
  requestID,
  maxNumberOfQuerySuggestions,
  strictLocalize,
}: {
  queryObject: Partial<Record<string, ISearchProductsRequest>>;
  sectionName: string;
  requestID?: string;
  maxNumberOfQuerySuggestions: number;
  strictLocalize: StrictLocalizeFunction;
}): ISuggestionEntry<string | Partial<ISuggestionSearchDataType>>[] => {
  if (!queryObject || Object.keys(queryObject).length === 0) {
    return [];
  }

  const queryObjectKeys: string[] = Object.keys(queryObject);
  const queryObjectValues: ISearchProductsRequest[] = Object.values(
    queryObject
  ) as ISearchProductsRequest[];

  // Flatten queryObjectValues
  const searchProductsRequests: IFlattenedSearchProductsRequest[] =
    flattenQueryObjectValues(
      queryObjectValues,
      queryObjectKeys,
      maxNumberOfQuerySuggestions
    );

  const queryData = [];
  for (const suggestion of searchProductsRequests) {
    // Can't build a ISuggestionEntry if query doesn't exist
    if (!suggestion.query) {
      continue;
    }

    // Create a new ISuggestionEntry for each IFlattenedSearchProductsRequest
    queryData.push(
      ISuggestionEntry.build({
        label: decodeMultimodalQueryText(suggestion.queryKey),
        url: buildLocalizedUrlWithQueryParams(
          suggestion,
          requestID,
          suggestion.category_key,
          strictLocalize
        ),
        sectionName,
        searchData: suggestion,
        requestID,
      })
    );
  }

  return queryData;
};

const categoryQueriesSuggestionArray = (
  categoryQueries: Partial<Record<string, ISearchMakersWithFiltersRequest>>,
  sectionName: string,
  requestId?: string
): ISuggestionEntry<string>[] =>
  Object.keys(categoryQueries ?? {})
    .filter((suggestionKey) => categoryQueries[suggestionKey]?.category)
    .map((suggestionKey) => {
      const makersWithFiltersRequest = categoryQueries[suggestionKey]!;
      // `suggested_category_queries` only returns with category = "All Products"
      // This is only returned, when the locale is English.
      // https://fairewholesale.atlassian.net/browse/FD-40344?focusedCommentId=57566
      // Hence, we do not need to make any change here atm.
      const category = makersWithFiltersRequest.category!;
      const obj = {
        label: suggestionKey,
        url: localizedUrlWithQueryParams(
          c1CategoryPath(category, {
            filters: [buildFilterKeys(makersWithFiltersRequest.filter_keys)],
          }),
          {
            refType:
              ISearchRequestReferrer.SearchRequestReferrerType
                .SUGGESTIONS_CATEGORY_QUERIES,
            refReqId: requestId,
          }
        ),
        sectionName,
        searchData: "",
      };
      return obj;
    });

const searchesSuggestionArray = (
  searchesArray: string[],
  sectionName: string,
  type: ISearchRequestReferrer.SearchRequestReferrerType,
  requestId: string | undefined
): ISuggestionEntry<string>[] =>
  searchesArray && searchesArray.length > 0
    ? searchesArray.map((searchesValue: string) => ({
        label: decodeMultimodalQueryText(searchesValue),
        url: localizedUrlWithQueryParams(productSearch({ q: searchesValue }), {
          refType: type,
          refReqId: requestId,
        }),
        sectionName,
        searchData: searchesValue,
        requestID: requestId,
      }))
    : [];

export const buildNaturalLanguageSuggestions = (
  suggestedNaturalLanguageSearchQueries: string[],
  sectionName: string,
  requestId: string | undefined
) => {
  const naturalLanguageSuggestionArray = searchesSuggestionArray(
    suggestedNaturalLanguageSearchQueries,
    sectionName,
    ISearchRequestReferrer.SearchRequestReferrerType
      .SUGGESTIONS_NATURAL_LANGUAGE_SEARCH_EMPTY_STATE,
    requestId
  );
  return naturalLanguageSuggestionArray;
};

const buildRecentSearches = (
  recentSearches: string[],
  sectionName: string,
  requestId: string | undefined
) => {
  const recentSearchesSuggestionArray = searchesSuggestionArray(
    recentSearches,
    sectionName,
    ISearchRequestReferrer.SearchRequestReferrerType
      .SUGGESTIONS_RECENT_SEARCHES,
    requestId
  );

  return recentSearchesSuggestionArray;
};

export const buildSuggestedTerms = (
  suggestionsResult: ISearchSuggestionResponse,
  sectionName: string,
  shouldEnableSuggestionsImpressionAllocationV2: boolean,
  strictLocalize: StrictLocalizeFunction
) => {
  const numberOfOverflowQuerySuggestions = Math.max(
    0,
    getSettingMaxNumBrandSuggestionsConfig() -
      suggestionsResult.suggested_brands?.length
  );
  const increasedNumberOfQuerySuggestions = Math.min(
    getSettingMaxNumSearchQuerySuggestionsConfig() +
      MAX_DESKTOP_SUGGESTION_QUERY_OVERFLOW,
    getSettingMaxNumSearchQuerySuggestionsConfig() +
      numberOfOverflowQuerySuggestions
  );
  const maxNumberOfQuerySuggestions =
    shouldEnableSuggestionsImpressionAllocationV2
      ? increasedNumberOfQuerySuggestions
      : MAX_SECTION_RESULT_SIZE;

  const maxNumberOfQueryAndCategorySuggestions =
    shouldEnableSuggestionsImpressionAllocationV2
      ? increasedNumberOfQuerySuggestions
      : MAX_SUGGESTION_ITEMS_PER_SECTION;

  const suggestedSearchQueriesSuggestionArray = buildQueriesSuggestionArray({
    queryObject: suggestionsResult.suggested_search_queries ?? {},
    sectionName,
    requestID: suggestionsResult.request_id,
    maxNumberOfQuerySuggestions,
    strictLocalize,
  })
    .concat(
      categoryQueriesSuggestionArray(
        suggestionsResult.suggested_category_queries ?? {},
        sectionName,
        suggestionsResult.request_id
      )
    )
    .slice(0, maxNumberOfQueryAndCategorySuggestions);

  return suggestedSearchQueriesSuggestionArray;
};

export const buildBrandSuggestions = (
  brandArray: ISuggestedBrand[],
  sectionName: string,
  requestId?: string
): ISuggestionEntry[] =>
  brandArray && brandArray.length > 0
    ? brandArray
        .slice(0, MAX_SUGGESTION_BRAND_SUGGESTIONS)
        .map((brandValue: ISuggestedBrand) => ({
          label: brandValue.name!,
          url: brandPage(brandValue.token!, {
            refType:
              ISearchRequestReferrer.SearchRequestReferrerType
                .SUGGESTIONS_BRANDS,
            refReqId: requestId,
          }),
          sectionName,
          searchData: brandValue,
          requestID: requestId,
        }))
    : [];

const trendingSearchUrl = (searchValue: string, refReqId?: string) => {
  if (isLoggedInRetailer()) {
    return localizedUrlWithQueryParams(productSearch({ q: searchValue }), {
      refType:
        ISearchRequestReferrer.SearchRequestReferrerType
          .SUGGESTIONS_TRENDING_SEARCHES,
      refReqId,
    });
  }
  return discoverSearch(searchValue.toLowerCase());
};

const buildTrendingSearches = (
  searchRecommendations: ISearchSuggestionResponse.ISearchRecommendation[],
  sectionName: string,
  requestId?: string
) =>
  searchRecommendations?.map(
    (searchValue: ISearchSuggestionResponse.ISearchRecommendation) => ({
      label: searchValue.name ?? "",
      url: trendingSearchUrl(searchValue.name ?? "", requestId),
      sectionName,
      searchData: {
        image: searchValue.image,
      },
      requestID: requestId,
    })
  );

export const buildProductSuggestions = (
  productArray: ISuggestedProduct[],
  sectionName: string
) =>
  productArray && productArray.length > 0
    ? productArray.map((productValue: ISuggestedProduct) => ({
        label: productValue.name!,
        url: `/product/${productValue.token}`,
        sectionName,
        searchData: productValue,
      }))
    : [];

const buildSearchesSuggestionArray = (
  searchesArray: string[],
  sectionName: string,
  type: ISearchRequestReferrer.SearchRequestReferrerType,
  requestId: string | undefined
): ISuggestionEntry<string>[] =>
  searchesArray && searchesArray.length > 0
    ? searchesArray.map((searchesValue: string) => ({
        label: decodeMultimodalQueryText(searchesValue),
        url: localizedUrlWithQueryParams(productSearch({ q: searchesValue }), {
          refType: type,
          refReqId: requestId,
        }),
        sectionName,
        searchData: searchesValue,
        requestID: requestId,
      }))
    : [];

export const buildEmptyStateSuggestionsSections = (
  suggestionsResult: ISearchSuggestionResponse,
  strictLocalize: StrictLocalizeFunction,
  shouldShowNLSSearchSuggestions: boolean
): ISuggestionSections => {
  const suggestionSections: ISuggestionSections = {};

  if (shouldShowNLSSearchSuggestions) {
    suggestionSections[
      getNaturalLanguageSearchEmptyStateHeader(strictLocalize)
    ] = buildNaturalLanguageSuggestions(
      suggestionsResult.suggested_natural_language_search_queries,
      getNaturalLanguageSearchEmptyStateHeader(strictLocalize),
      suggestionsResult.request_id
    );
  }

  // Recent searches
  suggestionSections[getRecentSearchesHeader(strictLocalize)] =
    buildRecentSearches(
      suggestionsResult.recent_searches,
      getRecentSearchesHeader(strictLocalize),
      suggestionsResult.request_id
    );

  // Trending searches
  // Show new trending searches section if search recommendations is returned
  if (
    shouldShowSearchRecommendations(suggestionsResult.search_recommendations)
  ) {
    suggestionSections[getTrendingSearchesHeader(strictLocalize)] =
      buildTrendingSearches(
        suggestionsResult.search_recommendations ?? [],
        getTrendingSearchesHeader(strictLocalize),
        suggestionsResult.request_id
      ).slice(0, MAX_SEARCH_RECOMMENDATION_PER_SECTION);
  } else {
    // Show the old trending searches sections if no search recommendations are returned
    suggestionSections[getTrendingSearchesHeader(strictLocalize)] =
      buildSearchesSuggestionArray(
        suggestionsResult.trending_searches ?? [],
        getTrendingSearchesHeader(strictLocalize),
        ISearchRequestReferrer.SearchRequestReferrerType
          .SUGGESTIONS_TRENDING_SEARCHES,
        suggestionsResult.request_id
      ).slice(0, MAX_SPLIT_SECTION_RESULT_SIZE);
  }

  return suggestionSections;
};

export const buildTypeaheadSuggestionsSections = (
  suggestionsResult: ISearchSuggestionResponse,
  strictLocalize: StrictLocalizeFunction,
  shouldEnableSuggestionsImpressionAllocationV2: boolean,
  viewport: Viewport
): ISuggestionSections => {
  const suggestionSections: ISuggestionSections = {};

  const numberOfTrendingBrands = MAX_SUGGESTION_ITEMS_PER_SECTION;
  const numberOfOverflowBrandSuggestions = Math.max(
    0,
    getSettingMaxNumSearchQuerySuggestionsConfig() -
      Object.keys(suggestionsResult.suggested_search_queries).length
  );
  const increasedNumberOfBrandSuggestions = Math.min(
    getSettingMaxNumBrandSuggestionsConfig() +
      MAX_DESKTOP_SUGGESTION_BRANDS_OVERFLOW,
    getSettingMaxNumBrandSuggestionsConfig() + numberOfOverflowBrandSuggestions
  );
  const maxNumberOfBrandSuggestions =
    shouldEnableSuggestionsImpressionAllocationV2
      ? increasedNumberOfBrandSuggestions
      : numberOfTrendingBrands;

  // Suggestions that go both to search and to browse with filters.
  suggestionSections[getSuggestedTermsHeader(strictLocalize)] =
    buildSuggestedTerms(
      suggestionsResult,
      getSuggestedTermsHeader(strictLocalize),
      shouldEnableSuggestionsImpressionAllocationV2,
      strictLocalize
    );

  suggestionSections[getBrandsHeader(strictLocalize)] = buildBrandSuggestions(
    suggestionsResult.suggested_brands ?? [],
    getBrandsHeader(strictLocalize),
    suggestionsResult.request_id
  ).slice(0, maxNumberOfBrandSuggestions);

  if (viewport.isMobile && isLoggedInRetailer()) {
    // Top Results
    suggestionSections[getTopResultsHeader(strictLocalize)] =
      buildProductSuggestions(
        suggestionsResult.top_search_results ?? [],
        getTopResultsHeader(strictLocalize)
      ).slice(0, MAX_SUGGESTION_TOP_RESULTS);
  }

  return suggestionSections;
};
